import React from 'react';
import ReactDOM, { render } from 'react-dom';
import { Provider, useSelector } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app, { createReducer } from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import LiveEditing from './Components/LiveEditing';
import MiniCart from './Components/MiniCart';
import MiniCartContainer from './Containers/MiniCart.Container';
import QuickSearch from './Components/QuickSearch';
import Navigation from './Components/Navigation';
import FacetedSearch from './Components/FacetedSearch';
import FacetedSearchCompactContainer from './Components/FacetedSearchCompactContainer';
import DynamicComponent from './Components/DynamicComponent';
import { ToastContainer } from 'react-toastify';
import LazyLoad from 'vanilla-lazyload';
import StyledAccessoryButton from './Components/AccessoryBuyButton';
import { initializeSentry } from './Services/Instrument';

import './Containers/VanillaContainer';
import { addOnClickEventListeners as addProductOnClickListener } from './Services/Product.service';
import { mountPriceHistory } from './Helpers/mountPriceHistory';
import NewNavigation from './Components/NavigationContainer';
import NavigationContainer from './Components/NavigationContainer';
import LanguageSelector from './Components/LanguageSelector';
import LanguageDropdown from './Components/LanguageDropdown';

const SentryInitializer = () => {
  const sentryDsn = useSelector((state) => state.sentryDsn);
  React.useEffect(() => {
    initializeSentry(sentryDsn);
  }, [sentryDsn]);

  return null;
};

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
const store = createStore(
  app,
  preloadState,
  composeWithDevTools(applyMiddleware(thunk, historyMiddleware))
);

// Add a dictionary to keep track of the registered async reducers
store.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
store.injectReducer = (key, asyncReducer) => {
  if (!store.asyncReducers[key]) {
    store.asyncReducers[key] = asyncReducer;
    store.replaceReducer(createReducer(store.asyncReducers));
  }
};

window.__litium = {
  ...window.__litium,
  bootstrapComponents: () => {
    // bootstrap React components, in case the HTML response we receive from the server
    // has React components. ReactDOM.render performs only an update on previous rendered
    // components and only mutate the DOM as necessary to reflect latest React element.
    bootstrapComponents();
  },
  cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
  registeredContainers.push(container);
  ReactDOM.render(element, container, callback);
};

window.onunload = () => {
  // make sure components are unmounted, redux's listener are unsubscribed
  // to fix issue with iframe navigation in IE
  registeredContainers.forEach((container) => {
    ReactDOM.unmountComponentAtNode(container);
  });
};

const bootstrapComponents = () => {
  ReactDOM.render(
    <Provider store={store}>
      <SentryInitializer />
    </Provider>,
    document.createElement('div') // Temporary placeholder, since this component doesn't render anything
  );

  if (document.getElementById('liveEditing')) {
    renderReact(
      <Provider store={store}>
        <LiveEditing />
      </Provider>,
      document.getElementById('liveEditing')
    );
  }

  if (document.getElementById('miniCart')) {
    renderReact(
      <Provider store={store}>
        <MiniCartContainer />
      </Provider>,
      document.getElementById('miniCart')
    );
  }

  const quickSearchElems = document.querySelectorAll('#quickSearch');

  if (quickSearchElems.length > 0) {
    quickSearchElems.forEach((item) => {
      const { vma } = item.dataset;
      renderReact(
        <Provider store={store}>
          <QuickSearch isCampaignBannerActive={vma == 'True'} />
        </Provider>,
        item
      );
    });
  }
  if (document.getElementById('language-selector')) {
    const { languages } = document.getElementById('language-selector').dataset;
    renderReact(
      <Provider store={store}>
        <LanguageSelector languageList={JSON.parse(languages)} />
      </Provider>,
      document.getElementById('language-selector')
    );
  }
  if (document.getElementById('navbar')) {
    const { block, additionalheaderlinks } = document.getElementById(
      'navbar'
    ).dataset;
    renderReact(
      <Provider store={store}>
        <Navigation
          block={block}
          additionalHeaderLinks={JSON.parse(additionalheaderlinks)}
        />
      </Provider>,
      document.getElementById('navbar')
    );
  }
  if (document.getElementById('language-dropdown')) {
    const { languages } = document.getElementById('language-dropdown').dataset;
    if (languages) {
      renderReact(
        <Provider store={store}>
          <LanguageDropdown languageList={JSON.parse(languages)} />
        </Provider>,
        document.getElementById('language-dropdown')
      );
    }
  }
  if (document.getElementById('newNavbar')) {
    const { block, additionalheaderlinks, languages } = document.getElementById(
      'newNavbar'
    ).dataset;
    renderReact(
      <Provider store={store}>
        <NavigationContainer
          block={block}
          additionalHeaderLinks={JSON.parse(additionalheaderlinks)}
          languageList={languages ? JSON.parse(languages) : null}
        />
      </Provider>,
      document.getElementById('newNavbar')
    );
  }
  if (document.getElementById('facetedSearch')) {
    renderReact(
      <Provider store={store}>
        <FacetedSearch />
      </Provider>,
      document.getElementById('facetedSearch')
    );
  }
  if (document.getElementById('facetedSearchCompact')) {
    renderReact(
      <Provider store={store}>
        <FacetedSearchCompactContainer />
      </Provider>,
      document.getElementById('facetedSearchCompact')
    );
  }
  if (document.getElementById('myPagePersons')) {
    const PersonList = DynamicComponent({
      loader: () => import('./Components/PersonListContainer'),
    });
    import('./Reducers/MyPage.reducer').then(({ myPage }) => {
      store.injectReducer('myPage', myPage);
      renderReact(
        <Provider store={store}>
          <PersonList />
        </Provider>,
        document.getElementById('myPagePersons')
      );
    });
  }
  if (document.getElementById('myPageAddresses')) {
    const AddressList = DynamicComponent({
      loader: () => import('./Components/AddressListContainer'),
    });
    renderReact(
      <Provider store={store}>
        <AddressList />
      </Provider>,
      document.getElementById('myPageAddresses')
    );
  }
  if (document.getElementById('checkout')) {
    const Checkout = DynamicComponent({
      loader: () => import('./Components/Checkout'),
    });
    renderReact(
      <Provider store={store}>
        <Checkout />
      </Provider>,
      document.getElementById('checkout')
    );
  }
  if (document.getElementById('lightBoxImages')) {
    const LightboxImages = DynamicComponent({
      loader: () => import('./Components/LightboxImages'),
    });
    import('./Reducers/LightboxImages.reducer').then(({ lightboxImages }) => {
      store.injectReducer('lightboxImages', lightboxImages);
      const rootElement = document.getElementById('lightBoxImages');
      const images = Array.from(
        rootElement.querySelectorAll('a')
      ).map((img) => ({ src: img.dataset.src }));
      const thumbnails = Array.from(
        rootElement.querySelectorAll('a img')
      ).map((img) => ({ src: img.src }));
      renderReact(
        <Provider store={store}>
          <LightboxImages images={images} thumbnails={thumbnails} />
        </Provider>,
        document.getElementById('lightBoxImages')
      );
    });
  }

  if (document.querySelectorAll('.slider').length > 0) {
    const Slider = DynamicComponent({
      loader: () => import('./Components/Slider'),
    });
    Array.from(document.querySelectorAll('.slider')).forEach(
      (slider, index) => {
        const values = [...slider.querySelectorAll('.slider__block')].map(
          (block) => {
            return {
              image: block.dataset.image,
              url: block.dataset.url,
              text: block.dataset.text,
              actionText: block.dataset.actionText,
            };
          }
        );
        if (values.length > 0) {
          renderReact(<Slider values={values} />, slider);
        }
      }
    );
  }
  let skipLinksElem = document.getElementById('skipLinks');
  if (skipLinksElem) {
    const SkipLinks = DynamicComponent({
      loader: () => import('./Components/SkipLinks'),
    });
    renderReact(
      <Provider store={store}>
        <SkipLinks />
      </Provider>,
      skipLinksElem
    );
  }

  let takeOverElement = document.getElementById('takeover');
  if (takeOverElement) {
    const TakeOver = DynamicComponent({
      loader: () => import('./Components/TakeOver'),
    });
    const {
      desktopImageSrc,
      desktopImageAlt,
      mobileImageSrc,
      mobileImageAlt,
      desktopContentPosition,
      mobileContentPosition,
      linkUrl,
      linkTitle,
      desktopContent,
      mobileContent,
      logoUrl,
      useImageOverlay,
    } = takeOverElement.dataset;
    renderReact(
      <Provider store={store}>
        <TakeOver
          {...{
            desktopImageSrc,
            mobileImageSrc,
            desktopImageAlt,
            mobileImageAlt,
            desktopContentPosition,
            mobileContentPosition,
            linkUrl,
            linkTitle,
            desktopContent,
            mobileContent,
            logoUrl,
            useImageOverlay: useImageOverlay === 'True',
          }}
        />
      </Provider>,
      takeOverElement
    );
  }

  if (document.querySelectorAll('buy-button').length > 0) {
    const BuyButton = DynamicComponent({
      loader: () => import('./Components/BuyButton'),
    });
    Array.from(document.querySelectorAll('buy-button')).forEach((button) => {
      const {
        articleNumber,
        quantityFieldId,
        packageQuantity,
        href,
        cssClass,
        label,
      } = button.dataset;
      renderReact(
        <Provider store={store}>
          <BuyButton
            {...{
              label,
              articleNumber,
              quantityFieldId,
              packageQuantity,
              href,
              cssClass,
            }}
          />
        </Provider>,
        button
      );
    });
  }
  if (document.querySelectorAll('accessory-buy-button').length > 0) {
    const StyledAccessoryBuyButton = DynamicComponent({
      loader: () => import('./Components/AccessoryBuyButton'),
    });
    Array.from(document.querySelectorAll('accessory-buy-button')).forEach(
      (button) => {
        const {
          articleNumber,
          quantityFieldId,
          packageQuantity,
        } = button.dataset;
        renderReact(
          <Provider store={store}>
            <StyledAccessoryBuyButton
              {...{
                articleNumber,
                quantityFieldId,
                packageQuantity,
              }}
            />
          </Provider>,
          button
        );
      }
    );
  }

  if (document.querySelectorAll('reorder-button').length > 0) {
    const ReorderButton = DynamicComponent({
      loader: () => import('./Components/ReorderButton'),
    });
    Array.from(document.querySelectorAll('reorder-button')).forEach(
      (button) => {
        const { cssClass, orderId, title } = button.dataset;
        const label = button.innerText;
        renderReact(
          <Provider store={store}>
            <ReorderButton {...{ label, title, cssClass, orderId }} />
          </Provider>,
          button
        );
      }
    );
  }
  let vatPicker = document.getElementById('vat-picker');
  if (vatPicker) {
    const VatPicker = DynamicComponent({
      loader: () => import('./Components/VatPicker'),
    });
    const { vat } = vatPicker.dataset;
    renderReact(
      <Provider store={store}>
        <VatPicker vat={vat} />
      </Provider>,
      document.getElementById('vat-picker')
    );
  }

  let accessoryModal = document.getElementById('accessory-modal');
  if (accessoryModal) {
    const AccessoryModal = DynamicComponent({
      loader: () => import('./Components/AccessoryModal/AccessoryModal'),
    });
    renderReact(
      <Provider store={store}>
        <AccessoryModal />
      </Provider>,
      accessoryModal
    );
  }

  let newsletterPrompt = document.getElementById('newsletter-prompt__wrapper');
  if (newsletterPrompt) {
    const NewsletterPrompt = DynamicComponent({
      loader: () => import('./Containers/NewsletterPromptContainer'),
    });
    const {
      title,
      campaignbannertext,
      subtext,
      isLoncin,
    } = newsletterPrompt.dataset;
    renderReact(
      <Provider store={store}>
        <NewsletterPrompt
          title={title}
          campaignbannertext={campaignbannertext}
          subtext={subtext}
          isLoncin={isLoncin === 'True'}
        />
      </Provider>,
      newsletterPrompt
    );
  }

  let footerNewsletterOptin = document.getElementById(
    'newsletter-footer-optin'
  );
  if (footerNewsletterOptin) {
    const NewsletterOptin = DynamicComponent({
      loader: () => import('./Containers/NewsletterOptinFooter'),
    });
    renderReact(
      <Provider store={store}>
        <NewsletterOptin />
      </Provider>,
      footerNewsletterOptin
    );
  }

  let customerServiceForm = document.querySelectorAll(
    '.customer-service__form-wrapper'
  );
  if (customerServiceForm.length > 0) {
    const CustomerServiceForm = DynamicComponent({
      loader: () => import('./Components/CustomerServiceForm'),
    });
    Array.from(customerServiceForm).forEach((inForm) => {
      const { form } = inForm.dataset;
      renderReact(
        <Provider store={store}>
          <CustomerServiceForm form={JSON.parse(form)} />
        </Provider>,
        inForm
      );
    });
  }

  let faqBlockItem = document.querySelectorAll('.faq-block__item');
  if (faqBlockItem.length > 0) {
    const Faq = DynamicComponent({
      loader: () => import('./Components/Faq'),
    });
    Array.from(faqBlockItem).forEach((block) => {
      const { title, text } = block.dataset;
      renderReact(
        <Provider store={store}>
          <Faq
            {...{
              title,
              text,
            }}
          />
        </Provider>,
        block
      );
    });
  }

  let blog = document.getElementById('blog-page');
  if (blog) {
    const Blog = DynamicComponent({
      loader: () => import('./Components/Blog'),
    });
    const { items } = blog.dataset;
    renderReact(
      <Provider store={store}>
        <Blog items={JSON.parse(items)} />
      </Provider>,
      blog
    );
  }

  let search_pages = document.getElementById('search-result__pages');
  if (search_pages) {
    const PagesSearchResult = DynamicComponent({
      loader: () => import('./Components/PagesSearchResult'),
    });
    const { result } = search_pages.dataset;
    renderReact(
      <Provider store={store}>
        <PagesSearchResult items={JSON.parse(result)} />
      </Provider>,
      search_pages
    );
  }

  let search_categories = document.getElementById('search-result__categories');
  if (search_categories) {
    const PagesSearchResult = DynamicComponent({
      loader: () => import('./Components/PagesSearchResult'),
    });
    const { result } = search_categories.dataset;
    renderReact(
      <Provider store={store}>
        <PagesSearchResult items={JSON.parse(result)} />
      </Provider>,
      search_categories
    );
  }

  let search_manuals = document.getElementById('search-result__manuals');
  if (search_manuals) {
    const ManualsSearchResultTable = DynamicComponent({
      loader: () => import('./Components/ManualsSearchResultTable'),
    });
    const { result } = search_manuals.dataset;
    var jsonResult = JSON.parse(result);
    renderReact(
      <Provider store={store}>
        <ManualsSearchResultTable manualSearchResult={jsonResult} />
      </Provider>,
      search_manuals
    );
  }

  let productSliders = document.querySelectorAll('.product-slider-ref');
  if (productSliders && productSliders.length > 0) {
    const ProductSlider = DynamicComponent({
      loader: () => import('./Components/ProductSlider'),
    });

    for (var productSliderElem of productSliders) {
      renderReact(
        <Provider store={store}>
          <ProductSlider>
            {Array.from(productSliderElem.children)}
          </ProductSlider>
        </Provider>,
        productSliderElem
      );
    }
  }

  const imageGalleries = document.querySelectorAll('[data-image-gallery]');
  if (imageGalleries.length > 0) {
    const ImageGallery = DynamicComponent({
      loader: () => import('./Components/ImageGallery'),
    });
    imageGalleries.forEach((section) => {
      const root = section.querySelector('[data-gallery-lightbox-root]');
      if (!root || !root.dataset.galleryMedia) {
        return;
      }
      const media = JSON.parse(root.dataset.galleryMedia);
      const texts = root.dataset.galleryTexts
        ? JSON.parse(root.dataset.galleryTexts)
        : {};
      renderReact(
        <Provider store={store}>
          <ImageGallery media={media} container={section} texts={texts} />
        </Provider>,
        root
      );
    });
  }

  let manualsSearchContainer = document.getElementById(
    'manuals-page__s-module'
  );
  if (manualsSearchContainer) {
    const ManualsSearchModule = DynamicComponent({
      loader: () => import('./Containers/ManualsSearchModuleContainer'),
    });
    const { generalmanuals } = manualsSearchContainer.dataset;
    renderReact(
      <Provider store={store}>
        <ManualsSearchModule generalManuals={JSON.parse(generalmanuals)} />
      </Provider>,
      manualsSearchContainer
    );
  }

  // ProductPage

  if (document.getElementById('lightBoxImages')) {
    const LightboxImages = DynamicComponent({
      loader: () => import('./Components/LightboxImages'),
    });
    import('./Reducers/LightboxImages.reducer').then(({ lightboxImages }) => {
      store.injectReducer('lightboxImages', lightboxImages);
      const rootElement = document.getElementById('lightBoxImages');
      const images = Array.from(
        rootElement.querySelectorAll('a')
      ).map((img) => ({ src: img.dataset.src }));
      const thumbnails = Array.from(
        rootElement.querySelectorAll('a img')
      ).map((img) => ({ src: img.src }));
      renderReact(
        <Provider store={store}>
          <LightboxImages images={images} thumbnails={thumbnails} />
        </Provider>,
        document.getElementById('lightBoxImages')
      );
    });
  }

  let productPageCarousel = document.getElementById('product-page-carousel');
  if (productPageCarousel) {
    const ProductPageCarousel = DynamicComponent({
      loader: () => import('./Components/ProductPageCarousel'),
    });

    import('./Reducers/LightboxImages.reducer').then(({ lightboxImages }) => {
      store.injectReducer('lightboxImages', lightboxImages);
      renderReact(
        <Provider store={store}>
          <ProductPageCarousel>
            {Array.from(productPageCarousel.children)}
          </ProductPageCarousel>
        </Provider>,
        productPageCarousel
      );
    });
  }


  const googleMap = document.getElementById('google-maps-react-root');
  if (googleMap) {
    const GoogleMapContainer = DynamicComponent({
      loader: () => import('./Containers/GoogleMapContainer'),
    });
    const { markers, lang } = googleMap.dataset;

    ReactDOM.render(
      <Provider store={store}>
        <GoogleMapContainer language={lang} markers={JSON.parse(markers)} />
      </Provider>,
      googleMap
    );
  }

  if (document.getElementById('toast-container')) {
    ReactDOM.render(
      <ToastContainer position="bottom-right" pauseOnHover={false} />,
      document.getElementById('toast-container')
    );
  }

  mountPriceHistory(document, store);

  let product_page_tabs = document.getElementById('product-information__tabs');
  if (product_page_tabs) {
    const ProductPageTabsContainer = DynamicComponent({
      loader: () => import('./Containers/ProductPageTabsContainer'),
    });
    const {
      productInformationitems,
      longdescription,
      extradescription,
      technical,
      manuals,
      media,
      isLoncin,
    } = product_page_tabs.dataset;
    const accessoriesRoot = document.getElementById(
      'product-information__tabs--accessories'
    );
    const sparePartsRoot = document.getElementById(
      'product-information__tabs--spareparts'
    );

    renderReact(
      <Provider store={store}>
        <ProductPageTabsContainer
          productInformation={productInformationitems}
          longdescription={longdescription}
          extradescription={extradescription}
          technical={JSON.parse(technical)}
          manuals={JSON.parse(manuals)}
          media={JSON.parse(media)}
          accessories={Array.from(accessoriesRoot.children)}
          spareparts={Array.from(sparePartsRoot.children)}
          isLoncin={isLoncin === 'True'}
        />
      </Provider>,
      product_page_tabs
    );
  }
  if (document.getElementById('orderHistoryPage')) {
    const OrderList = DynamicComponent({
      loader: () => import('./Components/OrderHistoryListContainer'),
    });
    import('./Reducers/MyPage.reducer').then(({ myPage }) => {
      store.injectReducer('myPage', myPage);
      const { text } = document.getElementById('orderHistoryPage').dataset;
      renderReact(
        <Provider store={store}>
          <OrderList text={text} />
        </Provider>,
        document.getElementById('orderHistoryPage')
      );
    });
  }
  let ci = document.getElementById('import-categories');
  if (ci) {
    const ImportCategories = DynamicComponent({
      loader: () => import('./Components/ImportCategories'),
    });
    renderReact(
      <Provider store={store}>
        <ImportCategories />
      </Provider>,
      ci
    );
  }

  for (let productListElem of document.querySelectorAll(
    '.main-content [data-productlist]'
  )) {
    addProductOnClickListener(productListElem);
  }
};

bootstrapComponents();
new LazyLoad({});

// Unregister any previously installed service worker
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => registration.unregister());
  });
}
